import { CdkVirtualScrollViewport } from '@angular/cdk/scrolling';
import { Component, ViewChild } from '@angular/core';
import { ActivatedRoute, Router, convertToParamMap } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import {
  PanAppState,
  PlatformEnum,
  PlatformService,
  SESSION_STORAGE_KEYS,
} from '@panamax/app-state';
import { BrowserDimensions } from '@panamax/app-state/lib/models/platform.model';
import { MAX_INVOICE_SELECTION } from '@payments/constants/constants';
import { MakePaymentPageViewModel } from '@payments/models/make-payment-page-view-model';
import { TransactionRowText } from '@payments/models/payment-transaction';
import { PaymentsBasePage } from '@payments/payments-shared/payments-base/payments-base.page';
import { TransactionRowDesktopConstants } from '@payments/payments-shared/transaction-row/desktop/transaction-row-desktop.component';
import { CancelPendingPaymentsService } from '@payments/services/cancel-pending-payments/cancel-pending-payments.service';
import { InvoiceIsrService } from '@payments/services/invoice-isr.service';
import { PaymentsService } from '@payments/services/payments.service';
import { QuickFilter } from '@shared/components/usf-filtering-banner/model/quick-filter.model';
import { UsfFilteringModalComponent } from '@shared/components/usf-filtering-modal/usf-filtering-modal.component';
import { PageTitle } from '@shared/constants/page-titles.enum';
import { PATHS } from '@shared/constants/paths';
import {
  Filter,
  InvoiceFilterIds,
  InvoiceStoreService,
  SortDirection,
} from '@usf/ngrx-payment';
import {
  PaymentTransactionsViewModel,
  SortFields,
  Status,
} from '@usf/payment-types';
import { Observable, Subscription, firstValueFrom } from 'rxjs';
import { distinctUntilChanged, filter, skip, take } from 'rxjs/operators';
import {
  MakePaymentService,
  PaymentQueryAlertNames,
  PaymentQueryParams,
} from './services/make-payment.service';

@Component({
  selector: 'app-make-payment',
  templateUrl: './make-payment.page.html',
  styleUrls: ['./make-payment.page.scss'],
})
export class MakePaymentPage extends PaymentsBasePage {
  @ViewChild(CdkVirtualScrollViewport)
  virtualScrollViewport: CdkVirtualScrollViewport;

  readonly PlatformEnum = PlatformEnum;
  readonly SortDirection = SortDirection;
  readonly SortFields = SortFields;
  readonly PendingStatus = Status.PENDING;
  readonly InvoiceFilterIds = InvoiceFilterIds;
  readonly ColumnNames = TransactionRowDesktopConstants.ColumnNames;

  allInvoicesButtonExtraMargin = 0;
  browserDimensions: BrowserDimensions;
  featureFlagPayments$: Subscription;
  invoiceFilters: Filter[];
  isAllInvoicesButtonFixed = true;
  isInitialLoading = true;
  isMobile: boolean;
  isTouch$: Observable<boolean>;
  loadingSub$: Subscription;
  makePaymentPageVMSub$: Subscription;
  modal: UsfFilteringModalComponent;
  paymentCustomers$: Subscription;
  sortDirection: SortDirection;
  sortField: SortFields;
  title: string;
  transactionRowText: TransactionRowText;
  maxTransactionsSelectedMessage: string;
  vm$: Observable<MakePaymentPageViewModel>;
  canCancel = false;
  showFullPO = false;
  columnSizes: Map<
    TransactionRowDesktopConstants.ColumnNames,
    string | number
  > = new Map();
  quickFilters: QuickFilter[] = [
    {
      id: [Status.OVERDUE],
      title: 'Overdue',
      count: 0,
      color: 'red',
      selected: false,
      translationKey: 'i18n.payments.quickFilters.overdue',
    },
    {
      id: [Status.UNPAID, Status.UNUSED],
      title: 'Unpaid / Unused',
      count: 0,
      color: 'red',
      selected: false,
      translationKey: 'i18n.payments.quickFilters.unpaidUnused',
    },
    {
      id: [Status.PARTIALLY_PAID],
      title: 'Partially paid',
      count: 0,
      color: 'red',
      selected: false,
      translationKey: 'i18n.payments.quickFilters.partiallyPaid',
    },
    {
      id: [
        Status.PENDING,
        Status.PROCESSING,
        Status.CANCELLING,
        Status.AUTOPAY,
      ],
      title: 'Pending / Processing',
      count: 0,
      color: 'red',
      selected: false,
      translationKey: 'i18n.payments.quickFilters.pendingProcessing',
    },
  ];
  filtersAndSortSub$: Subscription;
  showOrderNumber = false;

  constructor(
    public router: Router,
    readonly route: ActivatedRoute,
    readonly paymentsService: PaymentsService,
    readonly panAppState: PanAppState,
    readonly platformService: PlatformService,
    public invoiceIsrService: InvoiceIsrService,
    public makePaymentService: MakePaymentService,
    private translateService: TranslateService,
    private invoiceStore: InvoiceStoreService,
    private cancelPaymentsService: CancelPendingPaymentsService,
  ) {
    super(router, route, paymentsService);
  }

  ionViewWillEnter() {
    super.ionViewWillEnter();

    this.resetFilters();

    const queryFilters = this.getFiltersFromParams();

    this.makePaymentService.setInitialSortAndFilter(queryFilters);
    this.vm$ = this.makePaymentService.getMakePaymentsViewModel$();
    this.browserDimensions = this.platformService.getBrowserDimensions();
    this.isTouch$ = this.platformService.isTouchExperience$;
    this.isMobile = this.platformService.platformType === PlatformEnum.mobile;
    this.panAppState.setPageTitle(
      this.translateService.instant(PageTitle.makePayment),
    );
    this.setTranslatedText();

    this.filtersAndSortSub$ = this.makePaymentService
      .getFiltersAndSortInformation$()
      .subscribe(res => {
        this.invoiceFilters = res.filters;
      });

    this.paymentCustomers$ = this.paymentsService
      .getSelectedPaymentCustomers()
      .pipe(filter(paymentCustomers => paymentCustomers.customers.length > 0))
      .subscribe(paymentCustomers => {
        if (
          this.queryParamMap.keys.length > 0 &&
          this.queryParamMap.has(PaymentQueryParams.alertName)
        ) {
          this.queryParamMap = convertToParamMap([]);
          return;
        }
        let newFilters = this.invoiceFilters.filter(
          invoiceFilter =>
            invoiceFilter.filterName !== InvoiceFilterIds.Customers,
        );
        newFilters.push({
          filterName: InvoiceFilterIds.Customers,
          value: paymentCustomers.customers,
        });
        this.makePaymentService.setFilter(newFilters);
        if (paymentCustomers.isSuperUser) {
          this.clearAllSelections();
        }
      });

    this.makePaymentPageVMSub$ = this.vm$.subscribe(vm => {
      this.makePaymentService.showPaymentsOutageMessage(vm.paymentHasOutage);
      this.showOrderNumber = vm.showOrderNumber;
      this.updateColumnSizes();
    });

    this.loadingSub$ = this.vm$
      .pipe(
        filter(vm => {
          if (!vm.isLoadingTransaction && vm.pageNum === 0) {
            return false;
          }
          return true;
        }),
        distinctUntilChanged(
          (x, y) => x.isLoadingTransaction === y.isLoadingTransaction,
        ),
      )
      .subscribe(vm => {
        this.isInitialLoading = vm.isLoadingTransaction;
        if (!vm.isLoadingTransaction) {
          this.loadingSub$?.unsubscribe();
        }
      });

    this.featureFlagPayments$ = this.cancelPaymentsService
      .canCancelPayment$()
      .subscribe(canCancel => {
        this.canCancel = canCancel;
      });
    this.updateColumnSizes();
  }

  ionViewDidEnter() {
    this.makePaymentService.trackPageLoad();
  }

  ionViewWillLeave() {
    this.featureFlagPayments$?.unsubscribe();
    this.loadingSub$?.unsubscribe();
    this.makePaymentPageVMSub$.unsubscribe();
    this.paymentCustomers$.unsubscribe();
    this.filtersAndSortSub$.unsubscribe();
  }

  selectTransactionRow(transactionVM: PaymentTransactionsViewModel) {
    this.vm$.pipe(take(1)).subscribe(vm => {
      if (!transactionVM.selected && vm.isMaxTransactionsSelected) {
        this.paymentsService.showToastMessage(
          this.maxTransactionsSelectedMessage,
        );
      } else {
        this.paymentsService.selectInvoice(transactionVM);
      }
    });
  }

  async selectAllInvoices() {
    const vm = await firstValueFrom(this.vm$);

    this.paymentsService.selectAllInvoices(
      this.maxTransactionsSelectedMessage,
      {
        restrictToOpen: this.makePaymentService.restrictToOpen,
      },
      MAX_INVOICE_SELECTION - vm.selectedTransactions.length,
    );
  }

  handleSort({
    sortField,
    sortDirection,
  }: {
    sortField: string;
    sortDirection: SortDirection;
  }) {
    this.sort(sortField as SortFields, sortDirection);
  }

  clearAllSelections() {
    this.paymentsService.clearSelectedInvoices();
  }

  downloadAllInvoices() {
    this.paymentsService.fetchInvoices(
      1,
      0,
      this.makePaymentService.restrictToOpen,
    );
    this.vm$.pipe(skip(1), take(1)).subscribe(vm => {
      if (vm.warning?.isWarning) {
        this.paymentsService.showToastMessage(
          vm.warning.messageText.toString(),
        );
        this.paymentsService.fetchInvoices(
          1,
          undefined,
          this.makePaymentService.restrictToOpen,
        );
        return;
      }
      this.paymentsService.downloadInvoices(
        this.title,
        vm.transactions,
        this.showOrderNumber,
      );
    });
  }

  downloadSelectedInvoices(transactions: PaymentTransactionsViewModel[]) {
    this.invoiceIsrService.mapInvoicesToShow(transactions);
  }

  goToAllInvoices() {
    this.router.navigate([PATHS.PAYMENTS_ALL_INVOICES]);
  }

  goToAllInvoicesWithCustomers() {
    this.vm$.pipe(take(1)).subscribe(vm => {
      const customersFilter = vm.filters.find(
        filter => filter.filterName === InvoiceFilterIds.Customers,
      );
      if (customersFilter) {
        sessionStorage.setItem(
          SESSION_STORAGE_KEYS.paymentsInvoiceFilters,
          JSON.stringify([customersFilter]),
        );
      }
      this.goToAllInvoices();
    });
  }

  goToAdjustPayment() {
    this.router.navigate([PATHS.PAYMENTS_ADJUST_PAYMENT]);
  }

  async openMoreOptions({
    event,
    transaction,
  }: {
    event: Event;
    transaction: PaymentTransactionsViewModel;
  }) {
    const buttons: any[] = [];
    if (transaction.isPayable) {
      buttons.push({
        text: this.translateService.instant('i18n.common.pay'),
        handler: () => {
          this.payInvoice(transaction);
          this.invoiceIsrService.dismissPopover();
        },
      });
    }

    this.cancelPaymentsService
      .createCancelOption(transaction, () => {
        this.paymentsService.fetchInvoices(
          1,
          undefined,
          this.makePaymentService.restrictToOpen,
        );
        this.invoiceStore.reloadStatusSummary(
          this.makePaymentService.restrictToOpen,
        );
        this.clearAllSelections();
      })
      .subscribe(cancelButton => {
        !!cancelButton && buttons.push(cancelButton);
        this.invoiceIsrService.openInvoiceOptions(
          {
            event,
            transaction,
          },
          buttons,
        );
      });
  }

  openShareDownload() {
    const buttons: any[] = [
      {
        text: this.translateService.instant('i18n.common.download'),
        handler: () => {
          this.downloadAllInvoices();
          this.invoiceIsrService.dismissPopover();
        },
      },
      {
        text: this.translateService.instant('i18n.common.share'),
        handler: () => {
          this.shareAllInvoices();
          this.invoiceIsrService.dismissPopover();
        },
      },
      {
        text: this.translateService.instant('i18n.common.cancel'),
        role: 'cancel',
        cssClass: 'cancel-button',
        handler: () => {},
      },
    ];

    this.makePaymentService.presentPaymentOptions(buttons, event);
  }

  payInvoice(invoice: PaymentTransactionsViewModel) {
    this.paymentsService.clearSelectedInvoices();
    this.paymentsService.selectInvoice(invoice);
    this.goToAdjustPayment();
  }

  paySelectedInvoices() {
    this.goToAdjustPayment();
  }

  printAllInvoices() {
    this.paymentsService.fetchInvoices(
      1,
      0,
      this.makePaymentService.restrictToOpen,
    );
    this.vm$.pipe(skip(1), take(1)).subscribe(vm => {
      if (vm.warning?.isWarning) {
        this.paymentsService.showToastMessage(
          vm.warning.messageText.toString(),
        );
        this.paymentsService.fetchInvoices(
          1,
          undefined,
          this.makePaymentService.restrictToOpen,
        );
        return;
      }
      const issueDates = this.getIssueDates(vm);
      this.paymentsService.printInvoices(
        this.title,
        vm.transactions,
        this.convertFilterDate(issueDates.fromIssueDate),
        this.convertFilterDate(issueDates.toIssueDate),
      );
    });
  }

  resetFilters() {
    this.sortField = this.makePaymentService.defaultFilter.fieldName;
    this.sortDirection = this.makePaymentService.defaultFilter.sortValue;
  }

  scrolledIndexChange(_event, pageNum: number, hasNextPage: boolean) {
    const end = this.virtualScrollViewport.getRenderedRange().end;
    const total = this.virtualScrollViewport.getDataLength();
    const nextPage = pageNum + 1;

    if (end === total && hasNextPage) {
      this.paymentsService.fetchInvoices(
        nextPage,
        undefined,
        this.makePaymentService.restrictToOpen,
      );
    }
  }

  setTranslatedText() {
    this.translateService
      .get('i18n.payments.makePayment.title')
      .pipe(take(1))
      .subscribe(_ => {
        this.title = this.translateService.instant(
          'i18n.payments.makePayment.title',
        );
        this.transactionRowText = {
          invoice: this.translateService.instant(
            'i18n.payments.transactionRow.invoice',
          ),
          custPo: this.translateService.instant(
            'i18n.payments.transactionRow.custPo',
          ),
          issueDate: this.translateService.instant(
            'i18n.payments.transactionRow.issueDate',
          ),
          issueDateCut: this.translateService.instant(
            'i18n.payments.transactionRow.issueDateCut',
          ),
          amount: this.translateService.instant(
            'i18n.payments.transactionRow.amount',
          ),
          dueDate: this.translateService.instant(
            'i18n.payments.transactionRow.dueDate',
          ),
          amountDue: this.translateService.instant(
            'i18n.payments.transactionRow.amountDue',
          ),
          dueDateCut: this.translateService.instant(
            'i18n.payments.transactionRow.dueDateCut',
          ),
          amountCut: this.translateService.instant(
            'i18n.payments.transactionRow.amountCut',
          ),
          amountDueCut: this.translateService.instant(
            'i18n.payments.transactionRow.amountDueCut',
          ),
          pay: this.translateService.instant('i18n.common.pay'),
          paymentAmount: this.translateService.instant(
            'i18n.payments.transactionRow.paymentAmount',
          ),
        };
        this.maxTransactionsSelectedMessage = this.translateService.instant(
          'i18n.payments.makePayment.maximumInvoicesSelected',
          { maximumSelected: MAX_INVOICE_SELECTION },
        );
      });
  }

  shareAllInvoices() {
    this.paymentsService.fetchInvoices(
      1,
      0,
      this.makePaymentService.restrictToOpen,
    );
    this.vm$.pipe(skip(1), take(1)).subscribe(vm => {
      if (vm.warning?.isWarning) {
        this.paymentsService.showToastMessage(
          vm.warning.messageText.toString(),
        );
        this.paymentsService.fetchInvoices(
          1,
          undefined,
          this.makePaymentService.restrictToOpen,
        );
        return;
      }
      const issueDates = this.getIssueDates(vm);
      this.paymentsService.shareInvoices(
        this.title,
        vm.transactions,
        this.convertFilterDate(issueDates.fromIssueDate),
        this.convertFilterDate(issueDates.toIssueDate),
      );
    });
  }

  sort(field: SortFields, direction: SortDirection = SortDirection.DESC) {
    if (field === this.sortField) {
      this.sortDirection =
        this.sortDirection === SortDirection.ASC
          ? SortDirection.DESC
          : SortDirection.ASC;
    } else {
      this.sortDirection = direction;
    }
    this.sortField = field;

    let sortFields = [{ fieldName: field, sortValue: this.sortDirection }];
    if (this.sortField !== SortFields.transaction) {
      sortFields.push({
        fieldName: SortFields.transaction,
        sortValue: SortDirection.DESC,
      });
    }

    this.makePaymentService.setSort(sortFields);
  }

  trackByIdx(i) {
    return i;
  }

  async viewInvoice(transaction: PaymentTransactionsViewModel) {
    this.invoiceIsrService.viewInvoice(
      transaction.isr.docID.toString(),
      transaction.transactionNumber,
      transaction.secondaryTransactionNumber,
    );
  }

  protected getFiltersFromParams(): Filter[] {
    if (
      !this.queryParamMap.has(PaymentQueryParams.alertName) ||
      this.queryParamMap.get(PaymentQueryParams.alertName) !==
        PaymentQueryAlertNames.paymentPastDue
    ) {
      return [];
    }
    return super.getFiltersFromParams();
  }

  poNumberClicked() {
    this.showFullPO = !this.showFullPO;
    this.updateColumnSizes();
  }

  updateColumnSizes() {
    this.columnSizes = TransactionRowDesktopConstants.columnSizes(
      false,
      false,
      true,
      this.showFullPO,
      this.showOrderNumber,
    );
  }
}
